import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { message } from "antd";
import { NotificationsAPI } from "../API/NotificationsApi";
import { openNotificationsStream } from "../API/notificationsStream";

const NotificationContext = createContext(null);

const INITIAL_THRESHOLD_CONFIG = {
  threshold: 10,
  conditionRule: "Defect Count > Configured Threshold",
  repeatOption: "A", // Option A: Notify only when crossing <= Threshold -> > Threshold
  emailEnabled: true,
  inAppEnabled: true,
  emailRecipients: "fleet-reliability@airline.com, techops-leads@airline.com",
  airlineName: "Southwest Airlines (SWA)",
};

export function NotificationProvider({ children }) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [thresholdConfig, setThresholdConfig] = useState(INITIAL_THRESHOLD_CONFIG);
  const closeStreamRef = useRef(null);
  const listenersRef = useRef(new Set());

  const refreshUnreadCount = useCallback(async () => {
    const res = await NotificationsAPI.unreadCount();
    if (res?.data?.status) {
      setUnreadCount(res.data.unread_total || 0);
    }
  }, []);

  const notifyListeners = useCallback((payload) => {
    listenersRef.current.forEach((listener) => listener(payload));
  }, []);

  // Consumers (bell popover, full page) subscribe so they can refetch their
  // own paginated view whenever a push event or a read/dismiss action lands.
  const subscribe = useCallback((listener) => {
    listenersRef.current.add(listener);
    return () => listenersRef.current.delete(listener);
  }, []);

  const connectStream = useCallback(() => {
    if (closeStreamRef.current) return;
    closeStreamRef.current = openNotificationsStream({
      onMessage: (payload) => {
        setUnreadCount((prev) => prev + 1);
        notifyListeners({ type: "push", payload });
      },
    });
    refreshUnreadCount();
  }, [notifyListeners, refreshUnreadCount]);

  const disconnectStream = useCallback(() => {
    closeStreamRef.current?.();
    closeStreamRef.current = null;
  }, []);

  const fetchList = useCallback((params = {}) => NotificationsAPI.list(params), []);

  const markRead = useCallback(
    async (ids) => {
      await NotificationsAPI.markRead({ ids });
      refreshUnreadCount();
      notifyListeners({ type: "update" });
    },
    [notifyListeners, refreshUnreadCount]
  );

  const markUnread = useCallback(
    async (ids) => {
      await NotificationsAPI.markUnread({ ids });
      refreshUnreadCount();
      notifyListeners({ type: "update" });
    },
    [notifyListeners, refreshUnreadCount]
  );

  const markAllRead = useCallback(async () => {
    await NotificationsAPI.markRead({ all: true });
    message.success("All notifications marked as read");
    refreshUnreadCount();
    notifyListeners({ type: "update" });
  }, [notifyListeners, refreshUnreadCount]);

  const dismissAll = useCallback(async () => {
    await NotificationsAPI.dismiss({ all: true });
    message.info("All notifications cleared");
    refreshUnreadCount();
    notifyListeners({ type: "update" });
  }, [notifyListeners, refreshUnreadCount]);

  const dismissOne = useCallback(
    async (id) => {
      await NotificationsAPI.dismiss({ ids: [id] });
      message.info("Notification removed");
      refreshUnreadCount();
      notifyListeners({ type: "update" });
    },
    [notifyListeners, refreshUnreadCount]
  );

  const updateThresholdConfig = useCallback((newConfig, options = {}) => {
    setThresholdConfig((prev) => ({ ...prev, ...newConfig }));
    if (options.silent) return;
    message.success("Defect threshold configuration updated successfully");
  }, []);

  // Local sandbox math only — the real threshold engine and its config live
  // on the backend; this just previews how the condition rule evaluates.
  const evaluateThresholdCondition = useCallback(
    ({ defectCount }) => {
      const currentThreshold = Number(thresholdConfig.threshold);
      const count = Number(defectCount);

      if (count > currentThreshold) {
        const delta = count - currentThreshold;
        return {
          satisfied: true,
          delta,
          message: `Condition satisfied: Count (${count}) > Threshold (${currentThreshold}). Exceeded by ${delta}.`,
        };
      }

      if (count === currentThreshold) {
        return {
          satisfied: false,
          message: `Condition NOT satisfied: Defect Count (${count}) = Threshold (${currentThreshold}). Condition requires Count > Threshold. No notification generated.`,
        };
      }

      return {
        satisfied: false,
        message: `Condition NOT satisfied: Defect Count (${count}) < Threshold (${currentThreshold}). No notification generated.`,
      };
    },
    [thresholdConfig]
  );

  const value = useMemo(
    () => ({
      unreadCount,
      thresholdConfig,
      connectStream,
      disconnectStream,
      subscribe,
      refreshUnreadCount,
      fetchList,
      markRead,
      markUnread,
      markAllRead,
      dismissAll,
      dismissOne,
      updateThresholdConfig,
      evaluateThresholdCondition,
    }),
    [
      unreadCount,
      thresholdConfig,
      connectStream,
      disconnectStream,
      subscribe,
      refreshUnreadCount,
      fetchList,
      markRead,
      markUnread,
      markAllRead,
      dismissAll,
      dismissOne,
      updateThresholdConfig,
      evaluateThresholdCondition,
    ]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotifications must be used within a NotificationProvider");
  }
  return context;
}
